<template>
  <div class="flex items-start gap-3">
    <div class="relative w-24 h-16 bg-gray-100 rounded border border-gray-200 overflow-hidden flex-shrink-0">
      <img
        v-if="modelValue"
        :src="modelValue"
        class="w-full h-full object-cover"
        alt="">
      <div
        v-else
        class="w-full h-full flex items-center justify-center text-xs text-gray-400">
        No image
      </div>
    </div>

    <div class="flex-1 space-y-2">
      <input
        :value="modelValue"
        type="text"
        placeholder="https://… or pick a file →"
        class="form-input w-full"
        @blur="$emit('update:modelValue', $event.target.value)">

      <div class="flex items-center gap-3 text-xs">
        <label class="cursor-pointer text-gray-900 hover:underline">
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            class="hidden"
            :disabled="uploading"
            @change="handleFile">
          {{ uploading ? `Uploading… ${progress}%` : 'Upload file' }}
        </label>
        <button
          type="button"
          class="btn btn-ghost btn-xs"
          :disabled="genPending"
          :title="modelValue ? 'Generate a new AI image for this slot (uses 1 credit)' : 'Generate an AI image for this slot (uses 1 credit)'"
          @click="onGenerate">
          <span v-if="genPending" class="w-3 h-3 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" aria-hidden="true" />
          {{ genPending ? 'Generating…' : (modelValue ? '✨ Regenerate' : '✨ Generate') }}
        </button>
        <span v-if="error" class="form-error">{{ error }}</span>
      </div>

      <input
        :value="alt"
        type="text"
        placeholder="Describe this image (alt text)"
        class="form-input w-full"
        aria-label="Image alt text"
        @blur="$emit('update:alt', $event.target.value)">
      <p class="form-helper">
        Alt text helps SEO and screen readers describe the image.
      </p>
    </div>
  </div>
</template>

<script setup>
import { inject, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  alt: { type: String, default: '' },
  defn: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['update:modelValue', 'update:alt', 'generate'])

const api = inject('templateEditorApi', null)
const uploading = ref(false)
const progress = ref(0)
const error = ref('')

// Per-slot AI generation. The parent kicks off the async job; we show a local
// "Generating…" state cleared when the new image lands (modelValue changes,
// after the editor refreshes content) or after a safety timeout.
const genPending = ref(false)
let genTimer = null
function onGenerate() {
  genPending.value = true
  clearTimeout(genTimer)
  genTimer = setTimeout(() => { genPending.value = false }, 90000)
  emit('generate')
}
watch(() => props.modelValue, () => { genPending.value = false; clearTimeout(genTimer) })

async function handleFile(event) {
  const file = event.target.files?.[0]
  if (!file) return
  if (!api) {
    error.value = 'Upload not available'
    return
  }

  uploading.value = true
  progress.value = 10
  error.value = ''

  try {
    const data = await api.uploadImage(file, props.defn?.label || '')
    progress.value = 100
    emit('update:modelValue', data.url)
  } catch (e) {
    error.value = e.message || 'Upload failed'
  } finally {
    uploading.value = false
    progress.value = 0
    // reset the input so re-selecting the same file fires change
    event.target.value = ''
  }
}
</script>
